import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ShareNetwork, WhatsappLogo, FacebookLogo, XLogo, EnvelopeSimple, Link as LinkIcon,
    Check, X as XIcon,
} from "@phosphor-icons/react";

/**
 * ShareMenu — public marketing "Share PawLink" button + fallback modal.
 *
 * Behaviour (per product spec):
 *   - Mobile with navigator.share  → open native OS share sheet FIRST.
 *   - Mobile without navigator.share OR desktop → open our own share modal.
 *   - We only expose real, working share endpoints: WhatsApp, Facebook, X, Email, Copy Link.
 *     Instagram / TikTok / YouTube are intentionally omitted because no reliable web
 *     "share intent" URL exists for them — showing a fake button would betray user trust.
 */
export default function ShareMenu({ t, url, title, text, className = "", buttonTestId = "share-cta" }) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const shareUrl = url || (
        process.env.REACT_APP_SITE_URL ||
        (typeof window !== "undefined" ? window.location.origin : "https://getpawlink.com")
    );
    const shareTitle = title || "PawLink";
    const shareText = text || t("install.share_message");

    const isMobile = typeof navigator !== "undefined" &&
        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
    const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

    const handleClick = async () => {
        if (isMobile && canNativeShare) {
            try {
                await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
                return;
            } catch (err) {
                // User cancelled or share failed — fall back to our own menu.
                if (err && err.name === "AbortError") return;
            }
        }
        setOpen(true);
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* ignore */ }
    };

    const targets = [
        {
            key: "whatsapp",
            label: t("install.share_whatsapp"),
            Icon: WhatsappLogo,
            color: "bg-[#25D366] text-white",
            href: `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`,
        },
        {
            key: "facebook",
            label: t("install.share_facebook"),
            Icon: FacebookLogo,
            color: "bg-[#1877F2] text-white",
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
        },
        {
            key: "x",
            label: t("install.share_x"),
            Icon: XLogo,
            color: "bg-black text-white",
            href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
        },
        {
            key: "email",
            label: t("install.share_email"),
            Icon: EnvelopeSimple,
            color: "bg-white/10 text-white",
            href: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`,
        },
    ];

    return (
        <>
            <button
                type="button"
                data-testid={buttonTestId}
                onClick={handleClick}
                className={`group inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/[0.06] hover:bg-white/[0.10] backdrop-blur px-6 py-4 text-white font-semibold transition-all duration-300 hover:-translate-y-0.5 ${className}`}
            >
                <ShareNetwork size={18} weight="fill" />
                {t("install.share_cta")}
            </button>

            <AnimatePresence>
                {open && (
                    <ShareModal
                        t={t}
                        targets={targets}
                        onClose={() => setOpen(false)}
                        onCopy={copyLink}
                        copied={copied}
                        url={shareUrl}
                    />
                )}
            </AnimatePresence>
        </>
    );
}

function ShareModal({ t, targets, onClose, onCopy, copied, url }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [onClose]);

    return (
        <div
            data-testid="share-modal"
            className="fixed inset-0 z-[70] grid place-items-center px-4"
            role="dialog"
            aria-modal="true"
        >
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            />
            <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.98 }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                className="relative w-full max-w-md rounded-3xl bg-[hsl(152,15%,10%)] border border-white/10 p-6 sm:p-7 shadow-2xl"
            >
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="share-modal-close"
                    className="absolute top-3 end-3 w-9 h-9 rounded-full bg-white/10 grid place-items-center text-white/80 hover:bg-white/15 hover:text-white transition"
                    aria-label={t("install.share_close")}
                >
                    <XIcon size={14} weight="bold" />
                </button>

                <div className="w-12 h-12 rounded-2xl bg-paw-terra grid place-items-center shadow-lg">
                    <ShareNetwork size={22} weight="fill" className="text-white" />
                </div>

                <h3 className="mt-4 font-display text-2xl sm:text-3xl text-white leading-tight tracking-tight">
                    {t("install.share_title")}
                </h3>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">
                    {t("install.share_sub")}
                </p>

                <div className="mt-6 grid grid-cols-4 gap-3">
                    {targets.map((tg) => (
                        <a
                            key={tg.key}
                            href={tg.href}
                            target={tg.key === "email" ? "_self" : "_blank"}
                            rel="noopener noreferrer"
                            data-testid={`share-${tg.key}`}
                            className="group flex flex-col items-center gap-2"
                            onClick={() => setTimeout(onClose, 300)}
                        >
                            <div className={`w-14 h-14 rounded-2xl ${tg.color} grid place-items-center shadow-md group-hover:scale-105 transition-transform`}>
                                <tg.Icon size={22} weight="fill" />
                            </div>
                            <span className="text-[11px] text-white/75 font-medium leading-tight text-center">
                                {tg.label}
                            </span>
                        </a>
                    ))}
                </div>

                <div className="mt-6 flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-1.5">
                    <div className="flex-1 min-w-0 px-3 py-2 text-xs text-white/70 truncate" dir="ltr">
                        {url}
                    </div>
                    <button
                        type="button"
                        onClick={onCopy}
                        data-testid="share-copy-link"
                        className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition ${
                            copied ? "bg-emerald-500 text-white" : "bg-paw-terra hover:bg-paw-terraDark text-white"
                        }`}
                    >
                        {copied ? <Check size={12} weight="bold" /> : <LinkIcon size={12} weight="bold" />}
                        {copied ? t("install.share_copied") : t("install.share_copy")}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
